<template>
    <div>
        <template v-if="loading">
            <x-layout>
                <x-flex xs12>
                    <loading-spinner class="compact"/>
                </x-flex>
            </x-layout>
        </template>

        <template v-else>
            <x-layout v-if="isDeclaration || isPlacingBrokerUserForEdit" class="question-set">
                <x-flex xs2 offset-xs1>
                    <h3 class="question-set-title">Policy</h3>
                </x-flex>

                <x-flex xs6 class="question-set-questions">
                    <x-container>
                        <x-layout v-if="isPlacingBrokerUserForEdit">
                            <x-flex xs6>
                                <x-text-field
                                    v-model="submission.client_reference"
                                    label="Client reference"
                                    placeholder="Client reference"
                                    maxlength="50"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex v-if="isDeclaration" xs6>
                                <entity-field :entities="facilityEntities" :submission="submission" />
                            </x-flex>

                            <x-flex v-else-if="isPlacingBrokerUserForEdit" xs6>
                                <x-select
                                    v-model="submission.placing_broker_contact_id"
                                    :items="clientAssignments"
                                    class="required"
                                    label="Placing broker contact"
                                    placeholder=" "
                                    :rules="[v => ruleRequired(v, 'Placing broker contact')]"
                                    append-icon="keyboard_arrow_down"
                                    item-value="broker_user_id"
                                    item-text="user_name"
                                    outline
                                    required
                                    :menu-props="{ offsetY: true }"
                                />
                            </x-flex>

                            <x-flex xs6>
                                <x-autocomplete
                                    v-model="submission.currency_id"
                                    :items="currencies"
                                    class="required"
                                    label="Currency"
                                    placeholder="Currency"
                                    item-value="id"
                                    item-text="name"
                                    :rules="[v => ruleRequired(v, 'Currency')]"
                                    append-icon="keyboard_arrow_down"
                                    outline
                                    :disabled="isInvoice"
                                    :menu-props="{ offsetY: true }"
                                />
                            </x-flex>
                        </x-layout>
                    </x-container>
                </x-flex>
            </x-layout>

            <x-layout v-if="hasObligor && submission.currency_id" class="question-set obligors">
                <x-flex xs2 offset-xs1>
                    <h3 class="question-set-title">Obligors</h3>
                </x-flex>

                <x-flex xs6 class="question-set-questions">
                    <x-container>
                        <x-layout v-for="(obligor, index) in submission.obligors" :key="index">
                            <x-flex>
                                <x-autocomplete
                                    @change="nullObligor(index)"
                                    v-model="obligor.country_id"
                                    :items="countries"
                                    class="required"
                                    label="Obligor country"
                                    placeholder="Country"
                                    item-value="id"
                                    item-text="name"
                                    :rules="[v => ruleRequired(v, 'Country')]"
                                    append-icon="keyboard_arrow_down"
                                    outline
                                />
                            </x-flex>

                            <x-flex>
                                <x-autocomplete
                                    v-model="obligor.pivot.obligor_id"
                                    :items="filteredObligors[index]"
                                    class="required"
                                    label="Obligor"
                                    placeholder="Obligor"
                                    item-value="id"
                                    item-text="name"
                                    :rules="[v => ruleRequired(v, 'Obligor')]"
                                    append-icon="keyboard_arrow_down"
                                    outline
                                />
                            </x-flex>

                            <x-flex>
                                <x-currency-text-field
                                    v-model="obligor.pivot.sub_limit"
                                    label="Sublimit"
                                    placeholder=" "
                                    :prefix="currencyCode"
                                    :rules="[
                                        () => ruleLessThan(obligor.pivot.sub_limit, 1000000000000, 'Sublimit'),
                                        () => ruleGreaterThanOrEqualTo(obligor.pivot.sub_limit, 0, 'Sublimit'),
                                        () => ruleMaxDecimalPlaces(obligor.pivot.sub_limit || 0, 2, 'Sublimit')
                                    ]"
                                    outline
                                    :optional="true"
                                />
                            </x-flex>

                            <x-flex class="grow-0">
                                <x-btn
                                    class="square-icon label-spacing"
                                    color="tertiary"
                                    small
                                    :disabled="submission.obligors.length === 1"
                                    @click="removeObligorSection(index)"
                                >
                                    <i class="material-icons-outlined">delete_forever</i>
                                </x-btn>
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex>
                                <x-btn
                                    color="tertiary"
                                    small
                                    block
                                    @click="addObligorSection"
                                >
                                    <i class="material-icons mr-2">add</i>

                                    Add another Obligor
                                </x-btn>
                            </x-flex>
                        </x-layout>
                    </x-container>
                </x-flex>
            </x-layout>

            <x-layout v-if="showGuarantorRadios" class="question-set guarantors">
                <x-flex xs2 offset-xs1>
                    <h3 class="question-set-title">Guarantors</h3>
                </x-flex>

                <x-flex xs6 class="question-set-questions">
                    <x-container>
                        <x-layout>
                            <x-flex xs12>
                                <x-radio-group
                                    v-model="showGuarantorFields"
                                    :row="true"
                                    class="required"
                                    :rules="[
                                        v => ruleRequired(v, 'Guarantor')
                                    ]"
                                >
                                    <template v-slot:label>
                                        Add guarantor?
                                    </template>

                                    <x-radio label="Yes" :value="true" />

                                    <x-radio label="No" :value="false" />
                                </x-radio-group>
                            </x-flex>
                        </x-layout>

                        <template v-if="showGuarantorFields">
                            <x-layout v-for="(guarantor, index) in submission.guarantors" :key="index">
                                <x-flex>
                                    <x-autocomplete
                                        v-model="guarantor.country_id"
                                        :items="countries"
                                        class="required"
                                        label="Guarantor country"
                                        placeholder="Country"
                                        item-value="id"
                                        item-text="name"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                    />
                                </x-flex>

                                <x-flex>
                                    <x-autocomplete
                                        v-model="guarantor.pivot.guarantor_id"
                                        :items="filteredGuarantors[index]"
                                        class="required"
                                        label="Guarantor"
                                        placeholder="Guarantor"
                                        item-value="id"
                                        item-text="name"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        :rules="[v => ruleRequired(v, 'Guarantor')]"
                                    />
                                </x-flex>

                                <x-flex class="grow-0">
                                    <x-btn
                                        class="square-icon label-spacing"
                                        color="tertiary"
                                        small
                                        :disabled="submission.guarantors.length === 1"
                                        @click="removeGuarantorSection(index)"
                                    >
                                        <i class="material-icons-outlined">delete_forever</i>
                                    </x-btn>
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex>
                                    <x-btn
                                        color="tertiary"
                                        small
                                        block
                                        @click="addGuarantorSection"
                                    >
                                        <i class="material-icons mr-2">add</i>

                                        Add another Guarantor
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </template>
                    </x-container>
                </x-flex>
            </x-layout>
        </template>
    </div>
</template>

<script>
    import EntityField from '~/components/Broker/UIComponents/EntityField';
    import rules from '~/mixins/rules';
    import submissionQuoteHelpers from "../../../mixins/submissionQuoteHelpers";

    export default {
        name: 'EditableGenericQuestions',
        components: {EntityField},
        mixins: [rules, submissionQuoteHelpers],
        props: {
            value: {},
            productsProp: {
                type: Array,
                default: () => [],
            },
            currencies: {
                type: Array,
                default: () => [],
            },
            clientAssignments: {
                type: Array,
                default: () => [],
            },
            isPlacingBrokerUserForEdit: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                loading: true,
                products: [],
                countries: [],
                obligors: [],
                showGuarantorFields: null,
                multiFilteredGuarantors:[],
                nextKey: 1,
            }
        },
        computed: {
            submission: {
                get() {
                    return this.value;
                },
                set(submission) {
                    this.$emit('input', submission);
                }
            },
            filteredObligors() {
                const filteredObligors = [];
                this.submission.obligors.forEach((obligorRisk, index) => {
                    const selectedObligorIds = this.submission.obligors.slice(0, index).map(obligor => obligor.pivot.obligor_id);
                    const countryId = obligorRisk.country_id ? obligorRisk.country_id : this.submission.country_id;
                    filteredObligors[index] = this.obligors.filter(obligor => obligor.country_id === countryId &&
                        ![...this.selectedGuarantorIds, ...selectedObligorIds].includes(obligor.id));
                });
                return filteredObligors;
            },
            filteredGuarantors() {
                let filteredGuarantors = [];
                this.submission.guarantors.forEach((guarantorRisk, index) => {
                    const selectedGuarantorIds = this.submission.guarantors.slice(0, index).map(guarantor => guarantor.pivot.guarantor_id);
                    filteredGuarantors[index] = this.obligors.filter(obligor => obligor.country_id === guarantorRisk.country_id &&
                        ![...this.selectedObligorIds, ...selectedGuarantorIds].includes(obligor.id));
                });
                return filteredGuarantors;
            },
            selectedGuarantorIds() {
                return this.submission.guarantors.map(guarantor => guarantor.pivot.guarantor_id);
            },
            selectedObligorIds() {
                return this.submission.obligors.map(obligor => obligor.pivot.obligor_id);
            },
            showCountry() {
                return this.submission.product_id;
            },
            showObligor() {
                return this.showCountry && this.submission.country_id;
            },
            showGuarantorRadios() {
                const guarantorSelected = this.submission.guarantors?.length > 0 && this.submission.guarantors?.some(guarantor => guarantor.pivot.guarantor_id);

                if (guarantorSelected) {
                    return true;
                }

                return this.submission.obligors ? (this.showObligor || this.hasObligor) && this.submission.obligors[0]?.pivot.obligor_id : false;
            },
            showGuarantorCountry() {
                return this.showGuarantorRadios && this.showGuarantorFields;
            },
            hasObligor() {
                return !this.hasNoObligor;
            },
            facilityEntities() {
                return this.submission.facility?.active_entities || [];
            },
            isDeclaration() {
                return this.submission.risk_type === 'declaration';
            },
            isInvoice() {
                return this.submission.quote_type?.key === 'invoice';
            }
        },
        watch: {
            showGuarantorFields(value) {
                if (!value) {
                   this.resetGuarantors();
                }
            }
        },
        mounted() {
            let mountRequests = [];

            if (this.productsProp.length > 0) {
                this.products = this.productsProp;
            } else {
                mountRequests.push(axios.get(this.$root.api('product')).then(res => {
                    this.products = res.data.data
                }));
            }

            this.countries = this.$root.countries;

            mountRequests.push(axios.get(this.$root.api('obligor')).then(res => {
                this.obligors = res.data.data
            }));

            axios.all(mountRequests).then(() => {
                this.loading = false;
            });
            this.showGuarantorFields = this.submission.guarantors?.length ? this.submission?.guarantors[0].pivot.guarantor_id !== null : false;
            this.applyObligorKeys();
        },
        methods: {
            resetGuarantors() {
                this.submission.guarantors = [this.getDefaultGuarantor()];
            },
            showGuarantor(index) {
                return this.showGuarantorCountry && this.submission.guarantors[index].country_id !== null;
            },
            applyObligorKeys() {
                this.submission.obligors?.forEach(obligor => {
                    if (!obligor.key) {
                        obligor.key = this.getUniqueKey();
                    }

                    if (!obligor.pivot.sub_limit) {
                        obligor.pivot.sub_limit = null;
                    }
                });
                this.submission.guarantors?.forEach(guarantor => {
                    if (!guarantor.key) {
                        guarantor.key = this.getUniqueKey();
                    }
                });
                this.$forceUpdate();
            },
            nullObligor(index) {
                if (this.submission.obligors[index]?.pivot.obligor_id) {
                    this.submission.obligors[index].pivot.obligor_id = null;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .obligor-section,
    .guarantor-section {
        .layout {
            margin: auto -8px;

            .flex {
                padding: 8px;
            }
        }

        .x-input {
            :deep() {
                .x-input__prepend-inner {
                    margin: auto 0;
                    padding: 0 0 0 4px;
                    order: 1;

                    .x-icon {
                        color: $color-grey-5;

                        &:hover {
                            color: $color-red;
                        }
                    }
                }
            }
        }

        + .obligor-section,
        + .guarantor-section {
            border-top: 1px dashed rgba(112, 112, 112, 0.5);
            padding-top: 24px;
        }
    }
</style>
